import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'oidc-client-ts';
import { useAuth as useOidcAuth } from 'react-oidc-context';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const auth = useOidcAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Handle the authentication callback
    if (auth.isLoading) {
      return;
    }

    if (!isInitialized) {
      setIsInitialized(true);

      // If we're in a callback state, don't redirect
      if (auth.isAuthenticated || auth.activeNavigator) {
        return;
      }

      // If not authenticated and not in a callback state, redirect to login
      if (!auth.isAuthenticated && !window.location.pathname.includes('/login')) {
        navigate('/login');
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, isInitialized, navigate]);

  const login = () => {
    auth.signinRedirect();
  };

  const logout = () => {
    auth.removeUser();
    auth.signoutRedirect();
  };

  const getAccessToken = (): string | null => {
    return auth.user?.access_token || null;
  };

  const value = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user || null,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Custom hook to get the access token for API requests
export const useAuthHeader = () => {
  const { getAccessToken } = useAuth();

  return () => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
};